import { useState } from 'react'
import type { AlbumConfig, InvestmentDestination, StickerInvestment } from '../types'
import {
  formatCurrency,
  sequenceTarget,
  stickerValue,
  getThemeDefinition,
  themeDisplayName
} from '../data'
import { useI18n } from '../i18n/context'

const CURRENCY_OPTIONS = [
  { code: 'USD', label: 'USD — US Dollar ($)' },
  { code: 'BRL', label: 'BRL — Real Brasileiro (R$)' },
  { code: 'EUR', label: 'EUR — Euro (€)' },
  { code: 'GBP', label: 'GBP — British Pound (£)' },
  { code: 'JPY', label: 'JPY — Japanese Yen (¥)' },
  { code: 'CAD', label: 'CAD — Canadian Dollar (CA$)' },
  { code: 'AUD', label: 'AUD — Australian Dollar (A$)' },
  { code: 'CHF', label: 'CHF — Swiss Franc (CHF)' },
  { code: 'CNY', label: 'CNY — Chinese Yuan (¥)' },
  { code: 'INR', label: 'INR — Indian Rupee (₹)' },
  { code: 'MXN', label: 'MXN — Mexican Peso (MX$)' },
  { code: 'ARS', label: 'ARS — Argentine Peso ($)' }
]

const MAX_ALBUM_NAME_LENGTH = 80
const MAX_DESTINATION_NAME_LENGTH = 80
const MAX_DESTINATION_KIND_LENGTH = 60
const MAX_DESTINATION_NOTES_LENGTH = 240

interface Props {
  album: AlbumConfig
  stickers: Record<number, StickerInvestment>
  onUpdateAlbum: <K extends keyof AlbumConfig>(key: K, value: AlbumConfig[K]) => void
  onAddDestination: (destination: InvestmentDestination) => void
  onRemoveDestination: (id: string) => void
  headerActions?: JSX.Element
}

export function ConfigurationTab({
  album,
  stickers,
  onUpdateAlbum,
  onAddDestination,
  onRemoveDestination,
  headerActions
}: Props): JSX.Element {
  const { t } = useI18n()
  const theme = getThemeDefinition(album.theme)
  const [newInvestment, setNewInvestment] = useState({ name: '', kind: '', notes: '' })
  const [isTargetVisible, setIsTargetVisible] = useState(false)
  const targetAmount = sequenceTarget(album.size)
  const investedAmount = Object.values(stickers).reduce(
    (sum, s) => sum + stickerValue(s.stickerNumber, album.size),
    0
  )

  function handleAdd(): void {
    if (!newInvestment.name.trim() || !newInvestment.kind.trim()) return
    const destination: InvestmentDestination = {
      id: crypto.randomUUID(),
      name: newInvestment.name.trim(),
      kind: newInvestment.kind.trim(),
      notes: newInvestment.notes.trim()
    }
    onAddDestination(destination)
    setNewInvestment({ name: '', kind: '', notes: '' })
  }

  return (
    <>
      <section className="configuration-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">{t.configurationTitle}</p>
            <h2>{t.albumSettings}</h2>
          </div>
          {headerActions ? <div className="panel-heading-actions">{headerActions}</div> : null}
        </div>

        <div className="form-grid">
          <label className="field">
            <span>{t.albumName}</span>
            <input
              type="text"
              value={album.name}
              maxLength={MAX_ALBUM_NAME_LENGTH}
              onChange={(e) => onUpdateAlbum('name', e.target.value)}
            />
          </label>

          <label className="field">
            <span>{t.albumSize}</span>
            <input type="text" value={`${theme.size} ${t.stickersLabel}`} readOnly />
          </label>

          {!album.theme.startsWith('standard') && (
            <label className="field">
              <span>{t.theme}</span>
              <input type="text" value={themeDisplayName(theme, t)} readOnly />
            </label>
          )}

          <label className="field">
            <span>{t.targetFinishDateLabel}</span>
            <input
              type="date"
              value={album.targetFinishDate}
              onChange={(e) => onUpdateAlbum('targetFinishDate', e.target.value)}
            />
          </label>

          <label className="field">
            <span>{t.currency}</span>
            <select
              value={album.currency}
              onChange={(e) => onUpdateAlbum('currency', e.target.value)}
              aria-label={t.currency}
            >
              {CURRENCY_OPTIONS.map((opt) => (
                <option key={opt.code} value={opt.code}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="target-panel">
          <div className="target-heading">
            <div>
              <p className="eyebrow">{t.targetAmount}</p>
              <h2>{t.revealCalculation}</h2>
            </div>
            <button
              className="ghost-button"
              onClick={() => setIsTargetVisible((v) => !v)}
              type="button"
            >
              {isTargetVisible ? t.hideTotalTarget : t.showTotalTarget}
            </button>
          </div>

          {isTargetVisible ? (
            <div className="target-details">
              <strong>{formatCurrency(targetAmount, album.currency)}</strong>
              <p>
                {t.sequenceFormula}: {album.size} × ({album.size} + 1) / 2 = {targetAmount}
              </p>
              <p>
                {t.investedSoFar}: {formatCurrency(investedAmount, album.currency)} (
                {Math.round((investedAmount / targetAmount) * 100)}%)
              </p>
            </div>
          ) : (
            <p className="target-hidden-copy">{t.targetHidden}</p>
          )}
        </div>
      </section>

      <section className="configuration-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">{t.savedInvestments}</p>
            <h2>{t.preconfigureDestinations}</h2>
          </div>
        </div>

        <div className="destination-list">
          {album.savedInvestments.map((dest) => (
            <article className="destination-card" key={dest.id}>
              <div>
                <strong>{dest.name}</strong>
                <p>{dest.kind}</p>
                <small>{dest.notes || t.noNotes}</small>
              </div>
              <button
                className="ghost-button"
                onClick={() => onRemoveDestination(dest.id)}
                type="button"
              >
                {t.remove}
              </button>
            </article>
          ))}
        </div>

        <div className="add-destination-grid">
          <label className="field">
            <span>{t.destinationName}</span>
            <input
              type="text"
              value={newInvestment.name}
              maxLength={MAX_DESTINATION_NAME_LENGTH}
              onChange={(e) => setNewInvestment((c) => ({ ...c, name: e.target.value }))}
            />
          </label>
          <label className="field">
            <span>{t.typeLabel}</span>
            <input
              type="text"
              value={newInvestment.kind}
              maxLength={MAX_DESTINATION_KIND_LENGTH}
              onChange={(e) => setNewInvestment((c) => ({ ...c, kind: e.target.value }))}
            />
          </label>
          <label className="field field-wide">
            <span>{t.notesLabel}</span>
            <input
              type="text"
              value={newInvestment.notes}
              maxLength={MAX_DESTINATION_NOTES_LENGTH}
              onChange={(e) => setNewInvestment((c) => ({ ...c, notes: e.target.value }))}
            />
          </label>
        </div>

        <button className="primary-button" onClick={handleAdd} type="button">
          {t.addSavedInvestment}
        </button>
      </section>

      <section className="configuration-panel">
        <div className="panel-heading">
          <div>
            <p className="eyebrow">Legal</p>
            <h2>Disclaimer</h2>
          </div>
        </div>
        <div className="legal-disclaimer">
          <p>
            Legal Disclaimer This software is an unofficial, independent directory and information
            tool. It is not affiliated with, endorsed, sponsored, or specifically approved by
            Wizards of the Coast LLC or any third-party reference site.
          </p>
          <p>
            All character names, card names, game data, types, rules text, and statistics referenced
            within this application are the intellectual property of their respective owners. This
            application utilizes these plain-text names and factual attributes strictly for
            informational and identification purposes under the principles of Nominative Fair Use.
          </p>
          <p>
            Portions of the Magic: The Gathering materials used are property of Wizards of the
            Coast. (c) Wizards of the Coast LLC. This software is unofficial Fan Content permitted
            under the Wizards of the Coast Fan Content Policy. It is not approved or endorsed by
            Wizards.
          </p>
          <p>
            No official artwork, card frames, images, or media assets are packaged, hosted, or
            distributed by this software. All external links provided within this application direct
            users to publicly available, third-party websites independent of this project.
          </p>
        </div>
      </section>
    </>
  )
}
